import { type JSX } from 'react';
import { useIntl } from 'react-intl';

import { DataTableEmptyState } from './DataTableEmptyState.js';
import { useDataTable } from '../contexts/DataTableContext.js';
import { LoadingState } from '../features/LoadingState/LoadingState.js';
import { dataTableMessages } from '../messages.js';

/**
 * Renders state indicators for the `DataTable`, showing either a loading state
 * or an empty state when there is no data to display.
 */
export const DataTableStatePlaceholder = (props: {
  isColumnsEmpty: boolean;
  isDataEmpty: boolean;
  allColumnsHidden: boolean;
  emptyStateSlot: ReturnType<typeof DataTableEmptyState>;
}): JSX.Element | null => {
  const table = useDataTable();
  const intl = useIntl();

  const isLoading = table.getState().loading;

  if (isLoading) {
    return <LoadingState headerHeight={table.getTotalHeaderHeight()} />;
  }

  const { isColumnsEmpty, isDataEmpty, allColumnsHidden, emptyStateSlot } =
    props;

  if (isDataEmpty) {
    return emptyStateSlot ?? <DataTableEmptyState />;
  }

  if (isColumnsEmpty) {
    return (
      emptyStateSlot ?? (
        <DataTableEmptyState>
          {intl.formatMessage(dataTableMessages.noColumnsAvailable)}
        </DataTableEmptyState>
      )
    );
  }

  if (allColumnsHidden) {
    return (
      <DataTableEmptyState>
        {intl.formatMessage(dataTableMessages.noColumnsVisible)}
      </DataTableEmptyState>
    );
  }

  return null;
};
